import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, X, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

import { getErrorMessage } from '@/utils/errorHandler';
const ImageUploader = ({ images = [], onImagesChange, maxImages = 3, onUploadStatusChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length + images.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    if (onUploadStatusChange) onUploadStatusChange(true);
    const newImages = [...images];
    const token = localStorage.getItem('token');

    // Process each file sequentially to avoid overwhelming server
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const headers = {
          'Content-Type': 'multipart/form-data',
        };
        
        // Add auth header if token exists (to get better rate limits)
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await axios.post(`${API}/upload/image`, formData, { headers });
        const imageUrl = response.data?.url;

        if (typeof imageUrl === 'string') {
          newImages.push(imageUrl);
          onImagesChange(newImages); // Update immediately
          toast.success(`Uploaded ${file.name}`);
        } else {
          console.error("Invalid image response:", response.data);
          toast.error(`Server returned invalid image data for ${file.name}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(getErrorMessage(error, `Failed to upload ${file.name}`));
      }
    }

    setUploading(false);
    if (onUploadStatusChange) onUploadStatusChange(false);
    // Clear input
    e.target.value = '';
  };

  const removeImage = (indexToRemove) => {
    const newImages = images.filter((_, index) => index !== indexToRemove);
    onImagesChange(newImages);
  };

  if (!Array.isArray(images)) {
    console.error("ImageUploader received non-array images:", images);
    return <div className="text-red-500">Error: Invalid image list</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Label htmlFor="image-upload" className="cursor-pointer">
          <div className="flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-green-400 focus:outline-none">
            <div className="flex flex-col items-center space-y-2">
              {uploading ? (
                <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
              ) : (
                <UploadCloud className="w-8 h-8 text-gray-400" />
              )}
              <span className="font-medium text-gray-600">
                {uploading ? 'Uploading...' : 'Drop files to Attach, or browse'}
              </span>
              <span className="text-xs text-gray-500">
                Max {maxImages} images, 5MB each
              </span>
            </div>
          </div>
          <Input 
            id="image-upload" 
            type="file" 
            accept="image/*" 
            multiple 
            className="hidden" 
            onChange={handleFileSelect}
            disabled={uploading || images.length >= maxImages}
          />
        </Label>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {images.map((url, idx) => {
            // Safeguard: If url is somehow an object (e.g. error object), render it as text to prevent crash
            if (typeof url !== 'string') {
              console.error("Invalid image URL detected:", url);
              return (
                <div key={idx} className="relative group w-full h-24 flex items-center justify-center bg-red-50 border border-red-200 rounded-md p-2 overflow-hidden">
                  <span className="text-[10px] text-red-500 break-all">{JSON.stringify(url).slice(0, 100)}</span>
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            }

            return (
            <div key={idx} className="relative group">
              <img 
                src={url} 
                alt={`Uploaded ${idx + 1}`} 
                className="w-full h-24 object-cover rounded-md border border-gray-200" 
                onError={(e) => {
                  e.target.style.display = 'none';
                  // Safer way to show fallback without innerHTML modification if possible, or just hide
                  // For simplicity, let's just create a text node if possible, or use a sibling span
                  // Using innerHTML on parent is risky if parent has other children
                  // Better approach: React state for error tracking per image?
                  // Or just use a simpler DOM manipulation
                  const parent = e.target.parentElement;
                  const span = document.createElement('span');
                  span.className = "text-xs text-gray-500 flex items-center justify-center h-full w-full absolute inset-0";
                  span.textContent = "Image Load Failed";
                  parent.appendChild(span);
                }}
              />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
